import React, { useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import OrganizationTaskForm from './OrganizationTaskForm';

import { AppDispatch } from '../../stateManagement/store';
import { IOrganizationTask } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { deleteOrganizationTaskAsync, fetchOrganizationTasks, selectOrganizationTasks, selectOrganizationTasksLoading } from '../../stateManagement/organizationTaskSlice';
import { useTheme, useMediaQuery } from '@mui/material';
import { closeOrganizationTasksDialog, selectOrganizationTasksDialog } from '../../stateManagement/organizationTasksDialogSlice';

const OrganizationTaskListDialog: React.FC = () => {

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const organizationTasksDialog = useSelector(selectOrganizationTasksDialog);
    const organization = useSelector(selectSelectedOrganization);

    const organizationTasks = useSelector(selectOrganizationTasks);
    const loading = useSelector(selectOrganizationTasksLoading);

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    const dispatch = useDispatch<AppDispatch>();

    const [formOpen, setFormOpen] = useState<{ open: boolean; organizationTask?: IOrganizationTask | null }>({ open: false });

    const closeForm = () => {
        setFormOpen({ open: false });
    };

    const [deleteLoading, setDeleteLoading] = useState<string[]>([]);

    const handleDelete = (input: IOrganizationTask) => {
        setDeleteLoading([...deleteLoading, input.organizationTaskId])
        dispatch(deleteOrganizationTaskAsync({
            organizationId: input?.organizationId ?? "",
            organizationTaskId: input.organizationTaskId,
            currentIsDeleted: input.isDeleted ?? false,
        }))
            .unwrap()
            .catch((error) => {
                console.error(error, { variant: 'error' });
            })
            .finally(() => {
                setDeleteLoading(deleteLoading.filter((item) => item !== input.organizationTaskId))
            });
    };

    const serviceType: GridColumnType<IOrganizationTask> = {
        organizationTaskId: { type: "Id", dontShow: true },
        organizationId: { type: "Integer", dontShow: true },
        title: { type: "Text", header: 'Name', showInCreateRow: true },
        duration: { type: "Integer", header: 'Duration (minutes)', showInCreateRow: true },
        price: { type: "Integer", header: 'Price', showInCreateRow: true },
        isPublic: { type: 'Text', header: 'Is public' },
    };

    const handleNew = () => {
        setFormOpen({ open: true })
    };

    const handleEdit = (input: IOrganizationTask) => {
        setFormOpen({ open: true, organizationTask: input });
    };

    const handleClose = () => {
        setShowDeleted(false);
        dispatch(closeOrganizationTasksDialog());
    };

    useEffect(() => {
        if (organization && organization.organizationId) {
            dispatch(fetchOrganizationTasks({ organizationId: organization.organizationId, showDeleted }));
        }
    }, [dispatch, organization, showDeleted]);

    return (
        <Dialog
            open={organizationTasksDialog.open}
            onClose={() => handleClose()}
            fullWidth
            fullScreen={fullScreen} >
            {formOpen.open ?
                <OrganizationTaskForm organizationTaskInput={formOpen.organizationTask} closeForm={closeForm} />
                :
                <GridTable
                    gridTitle='Service list'
                    gridTitleInfo='Manage the list of services offered by this place'
                    loading={loading}
                    list={organizationTasks.map((item) => ({ ...item, id: item.organizationTaskId }))}
                    inputType={serviceType}
                    onClickNew={handleNew}
                    onEditRow={handleEdit}
                    onDeleteRow={handleDelete}
                    deleteLoading={deleteLoading}
                    onClickBack={handleClose}
                    isOnDialog={true}
                    showInfoToggleButton={true}
                    onClickDeleteFolder={onClickDeleteFolder}
                    showDeleted={showDeleted}
                />
            }
        </Dialog>
    );
};

export default OrganizationTaskListDialog;
